
import { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { toLocalDate } from "@/utils/dateUtils";
import { GuestAvailabilityModal } from "./GuestAvailabilityModal";

interface AvailabilityCount {
  morning: number;
  afternoon: number;
  evening: number;
  total: number;
}

interface DateAvailability {
  date: string;
  availability: AvailabilityCount;
  isPreferred: boolean;
  isSelected: boolean;
}

interface EventDateTimeTableProps {
  eventDates: string[];
  preferredTimeOfDay: string[];
  selectedDate?: string | null;
  guestAvailability: Record<string, Record<string, any[]>>;
}

interface ModalState {
  isOpen: boolean;
  date: string;
  timeSlot: string;
  guests: any[];
}

export const EventDateTimeTable = ({
  eventDates,
  preferredTimeOfDay,
  selectedDate,
  guestAvailability
}: EventDateTimeTableProps) => {
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    date: "",
    timeSlot: "",
    guests: []
  });

  // Process the dates and times into a format for display
  const processedDates: DateAvailability[] = eventDates.map(date => {
    const dateStr = date;
    const guestsForDate = guestAvailability[dateStr] || {};

    // Count guests for each time slot
    const availability: AvailabilityCount = {
      morning: guestsForDate.morning?.length || 0,
      afternoon: guestsForDate.afternoon?.length || 0,
      evening: guestsForDate.evening?.length || 0,
      total: 0
    };

    // Calculate total availability
    availability.total = availability.morning + availability.afternoon + availability.evening;
    return {
      date: dateStr,
      availability,
      isPreferred: true,
      // All dates in eventDates array are preferred
      isSelected: dateStr === selectedDate
    };
  });

  // Sort dates by total availability (descending) and then by date (ascending)
  const sortedDates = [...processedDates].sort((a, b) => {
    if (b.availability.total !== a.availability.total) {
      return b.availability.total - a.availability.total;
    }
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });

  const getAvailabilityColor = (count: number): string => {
    if (count === 0) return "bg-gray-100 text-gray-500";
    if (count < 3) return "bg-amber-100 text-amber-800";
    return "bg-green-100 text-green-800";
  };

  const openGuestModal = (date: string, timeSlot: string) => {
    // Only open the modal if there are guests available for this time slot
    const guestsForDate = guestAvailability[date] || {};
    const guestsForTimeSlot = guestsForDate[timeSlot] || [];
    
    if (guestsForTimeSlot.length === 0 || !preferredTimeOfDay.includes(timeSlot)) {
      return; // Don't open modal if no guests or if time slot not preferred
    }

    setModalState({
      isOpen: true,
      date,
      timeSlot,
      guests: guestsForTimeSlot
    });
  };

  const closeGuestModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  const renderAvailabilityBadge = (count: number, timeSlot: string, date: string) => {
    // Check if the time slot is in the preferred times
    const isPreferred = preferredTimeOfDay.includes(timeSlot);
    
    // If not preferred, show "-" instead of "0"
    if (!isPreferred) {
      return (
        <Badge variant="outline" className="rounded-md py-1 px-2 text-gray-400">
          -
        </Badge>
      );
    }

    // For preferred time slots, show the count with appropriate styling
    return (
      <Badge 
        variant="outline" 
        className={cn(
          "rounded-md py-1 px-2", 
          getAvailabilityColor(count),
          count > 0 && "cursor-pointer hover:opacity-80"
        )}
        onClick={() => count > 0 && openGuestModal(date, timeSlot)}
      >
        {count}
      </Badge>
    );
  };

  if (eventDates.length === 0) {
    return <div className="text-center py-6 text-gray-500">
        No dates have been specified for this event.
      </div>;
  }

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[120px]">
            </TableHead>
            <TableHead className="text-center">M</TableHead>
            <TableHead className="text-center">A</TableHead>
            <TableHead className="text-center">E</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedDates.map(dateInfo => {
            const formattedDate = format(toLocalDate(dateInfo.date), 'EEE, MMM d');
            return (
              <TableRow 
                key={dateInfo.date} 
                className={cn(
                  dateInfo.isSelected && "bg-blue-50", 
                  !dateInfo.isSelected && "hover:bg-gray-50"
                )}
              >
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {formattedDate}
                    {dateInfo.isSelected && (
                      <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                        Selected
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  {renderAvailabilityBadge(dateInfo.availability.morning, 'morning', dateInfo.date)}
                </TableCell>
                <TableCell className="text-center">
                  {renderAvailabilityBadge(dateInfo.availability.afternoon, 'afternoon', dateInfo.date)}
                </TableCell>
                <TableCell className="text-center">
                  {renderAvailabilityBadge(dateInfo.availability.evening, 'evening', dateInfo.date)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <GuestAvailabilityModal
        isOpen={modalState.isOpen}
        onClose={closeGuestModal}
        date={modalState.date}
        timeSlot={modalState.timeSlot}
        guests={modalState.guests}
      />
    </div>
  );
};
